import { action, computed, makeObservable } from "mobx";
import uuid from "react-native-uuid";
import type { Api } from "../../../core/apis";
import { BaseModel, type BaseModelData } from "../../../core/BaseModel";
import type { Stores } from "../../../core/stores";
import type { EditorValue } from "../../../ui/YooptaEditor";
import {
  type BusinessPlanModel,
  BusinessStatus,
} from "../../plan/BusinessPlanModel";
import type { BusinessTemplateModel } from "../templates/BusinessTemplateModel";

export enum CustomerMemberLevel {
  DEV = 1 << 0,
  BusinessAdmin = 1 << 1,
}

export type CustomerStaticSuperUserInfo = {
  memberLevel: CustomerMemberLevel | null; // Admin etc
  displayName: string;
  avatarUrl: string | null;
};

type CustomerStaticSuperUserInfoWithUserUid = CustomerStaticSuperUserInfo & {
  superUserUid: string;
};

export type CustomerThemeOptions = {
  pressablePrimaryLabel: string;
  pressablePrimaryBackground: string;
};

export type CustomerProfileData = {
  name: string;
  logoUrl: null | string;
  theme: null | CustomerThemeOptions;
  introText: EditorValue | null;
};

export type CustomerProfileQuestion = {
  uid: string;
  question: string;
};

export interface BusinessCustomerData extends BaseModelData {
  profile: CustomerProfileData;
  superUserInfo: { [key: string]: CustomerStaticSuperUserInfo };

  applying: { [key: string]: CustomerStaticSuperUserInfo };
  profileQuestions: CustomerProfileQuestion[];
}

export class BusinessCustomerModel extends BaseModel<BusinessCustomerData> {
  constructor(stores: Stores, api: Api, data: BusinessCustomerData) {
    super(stores, api, data);

    makeObservable(this);

    Object.assign(this.data, {
      applying: this.data.applying ?? {},
      profileQuestions: this.data.profileQuestions ?? [],
    });
  }

  @computed get hasAdminAccess(): boolean {
    const superUser = this.stores.sessionStore.superUser;

    if (!superUser || !this.data.superUserInfo[superUser.uid]) {
      return false;
    }

    const memberLevel = this.data.superUserInfo[superUser.uid].memberLevel;

    return !!((memberLevel ?? 0) & CustomerMemberLevel.BusinessAdmin);
  }

  @computed get admins(): CustomerStaticSuperUserInfoWithUserUid[] {
    return Object.keys(this.data.superUserInfo).map((s) => ({
      superUserUid: s,
      ...this.data.superUserInfo[s],
    }));
  }

  @computed get applyingUsers(): CustomerStaticSuperUserInfoWithUserUid[] {
    return Object.keys(this.data.applying).map((s) => ({
      superUserUid: s,
      ...this.data.applying[s],
    }));
  }

  @action async removeAdmin(superUserUid: string) {
    if (this.data.superUserInfo[superUserUid]) {
      delete this.data.superUserInfo[superUserUid];
      await this.api.businessCustomersApi.set(this);
    }
  }

  @action async removeProfileQuestion(question: CustomerProfileQuestion) {
    this.data.profileQuestions = this.data.profileQuestions.filter(
      (q) => q.uid !== question.uid,
    );
    await this.api.businessCustomersApi.set(this);
  }

  @action async addProfileQuestion(question: string) {
    if (!question.length) {
      return;
    }

    this.data.profileQuestions.push({
      uid: uuid.v4(),
      question,
    });
    await this.api.businessCustomersApi.set(this);
  }

  @action async updateProfileQuestion(question: CustomerProfileQuestion) {
    const foundQuestion = this.data.profileQuestions.find(
      (q) => q.uid === question.uid,
    );
    if (foundQuestion !== undefined) {
      foundQuestion.question = question.question;
      await this.api.businessCustomersApi.set(this);
    }
  }

  @action async addApplication(
    superUserUid: string,
    info: CustomerStaticSuperUserInfo,
  ) {
    if (
      !this.data.superUserInfo[superUserUid] &&
      !this.data.applying[superUserUid]
    ) {
      this.data.applying[superUserUid] = info;
      await this.api.businessCustomersApi.set(this);
    }
  }

  @action async acceptApplication(superUserUid: string) {
    if (
      !this.data.superUserInfo[superUserUid] &&
      this.data.applying[superUserUid]
    ) {
      this.data.superUserInfo[superUserUid] = this.data.applying[superUserUid];
      delete this.data.applying[superUserUid];
      await this.api.businessCustomersApi.set(this);
    }
  }

  @action async declineApplication(superUserUid: string) {
    if (
      !this.data.superUserInfo[superUserUid] &&
      this.data.applying[superUserUid]
    ) {
      delete this.data.applying[superUserUid];
      await this.api.businessCustomersApi.set(this);
    }
  }

  @action async addMemberLevels(
    superUserUid: string,
    memberLevels: CustomerMemberLevel,
  ) {
    // TODO - correct to fetch here?
    if (!this.data.superUserInfo[superUserUid]) {
      const superUser = await this.api.superUsersApi.getById(superUserUid);
      if (!superUser) {
        throw Error("Super user does not exist");
      }

      this.data.superUserInfo[superUserUid] = {
        memberLevel: memberLevels,
        avatarUrl: superUser.data.avatarUrl,
        displayName: superUser.data.displayName,
      };
    } else {
      this.data.superUserInfo[superUserUid].memberLevel =
        (this.data.superUserInfo[superUserUid].memberLevel ?? 0) | memberLevels;
    }

    await this.api.businessCustomersApi.update(this.uid, {
      superUserInfo: this.data.superUserInfo,
    });
  }

  @action async removeMemberLevels(
    superUserUid: string,
    memberLevels: CustomerMemberLevel,
  ) {
    this.data.superUserInfo[superUserUid].memberLevel =
      (this.data.superUserInfo[superUserUid].memberLevel ?? 0) & ~memberLevels;
    await this.api.businessCustomersApi.update(this.uid, {
      superUserInfo: this.data.superUserInfo,
    });
  }

  @computed get activeBusinessPlans(): BusinessPlanModel[] {
    const items: BusinessPlanModel[] = [];

    const data = this.stores.businessPlansStore.all;
    for (const d of data) {
      if (
        d.data.customerInfo.uid === this.uid &&
        d.data.status === BusinessStatus.Active
      ) {
        items.push(d);
      }
    }
    return items.sort((a, b) =>
      (a.data.ts?.seconds ?? 0) > (b.data.ts?.seconds ?? 0) ? 1 : -1,
    );
  }

  @computed get archivedBusinessPlans(): BusinessPlanModel[] {
    const items: BusinessPlanModel[] = [];

    const data = this.stores.businessPlansStore.all;
    for (const d of data) {
      if (
        d.data.customerInfo.uid === this.uid &&
        d.data.status === BusinessStatus.Archived
      ) {
        items.push(d);
      }
    }
    return items.sort((a, b) =>
      (a.data.ts?.seconds ?? 0) > (b.data.ts?.seconds ?? 0) ? 1 : -1,
    );
  }

  @computed get templates(): BusinessTemplateModel[] {
    const items: BusinessTemplateModel[] = [];

    const data = this.stores.businessTemplatesStore.all;
    for (const d of data) {
      if (d.data.customerUid === this.uid) {
        items.push(d);
      }
    }
    return items.sort((a, b) =>
      (a.data.ts?.seconds ?? 0) > (b.data.ts?.seconds ?? 0) ? -1 : 1,
    );
  }

  @action
  async updateTheme(theme: Partial<CustomerThemeOptions> | undefined) {
    if (theme) {
      this.data.profile.theme = {
        ...(this.data.profile.theme ?? {
          pressablePrimaryBackground: "#ffd84e",
          pressablePrimaryLabel: "#000000",
        }),
        ...theme,
      };
    } else {
      this.data.profile.theme = null;
    }

    this.api.businessCustomersApi.update(this.uid, this.data);
  }

  @action
  async updateLogo(logoUrl: string | null) {
    this.data.profile.logoUrl = logoUrl;
    this.api.businessCustomersApi.update(this.uid, this.data);
  }

  @action
  async updateIntroText(text: EditorValue | null) {
    this.data.profile.introText = text;
    this.api.businessCustomersApi.update(this.uid, this.data);
  }

  @action
  async updateProfile(p: Partial<CustomerProfileData>) {
    this.data.profile = { ...this.data.profile, ...p };
    this.api.businessCustomersApi.update(this.uid, {
      profile: this.data.profile,
    });
  }

  toFireStore(): Omit<BusinessCustomerData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
